import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowLeft,
  Building2,
  Calendar,
  CheckCircle2,
  Clock,
  FileBarChart,
  Mail,
  Play,
  Repeat,
  Send,
  Tag,
  X,
  XCircle,
} from 'lucide-react';
import { PreviewModal } from '../components/PreviewModal';
import { SendReportModal } from '../components/SendReportModal';
import { useAuth } from '../contexts/use-auth';
import { api, type ReportConfig, type ReportRun } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { SortableHeader } from '../components/ui/SortableHeader';
import { useSort } from '../lib/sort';
import { formatDateTime, formatNumber, formatRelative } from '../lib/format';
import { deriveStatus, DISPLAY_LABEL, DISPLAY_VARIANT, humanizeSchedule } from '../lib/reports';
import { cn } from '../lib/utils';

const getRunValue = (item: ReportRun, key: string): unknown => {
  switch (key) {
    case 'run_timestamp':
      return item.run_timestamp;
    case 'report_date':
      return item.report_date ?? '';
    case 'status':
      return deriveStatus(item);
    case 'row_count':
      return item.row_count ?? 0;
    case 'duration_ms':
      return item.duration_ms ?? 0;
    case 'triggered_by':
      return item.triggered_by ?? '';
    default:
      return undefined;
  }
};

export function ReportDetail() {
  const { reportId: rawReportId } = useParams<{ reportId: string }>();
  const reportId = rawReportId ? decodeURIComponent(rawReportId) : '';

  const configs = useQuery({
    queryKey: ['reports', 'configs'],
    queryFn: () => api.listReportConfigs(),
    staleTime: 5 * 60 * 1000,
  });

  // Scoped to this report so the API can Query the partition key instead of
  // scanning the whole audit table.
  const runs = useQuery({
    queryKey: ['reports', 'runs', reportId],
    queryFn: () => api.listReportRuns({ reportId }),
    staleTime: 60 * 1000,
  });

  const config: ReportConfig | undefined = useMemo(
    () => configs.data?.items.find((c) => c.report_id === reportId),
    [configs.data, reportId],
  );

  const reportRuns = useMemo(
    () => (runs.data?.items ?? []).filter((r) => r.report_id === reportId),
    [runs.data, reportId],
  );

  const { sortedItems, sort, toggle } = useSort(reportRuns, getRunValue);
  const [selected, setSelected] = useState<ReportRun | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Pinned once on mount rather than read during render: a re-render must not
  // silently shift the 30-day window and change the numbers under the reader.
  const [windowStart] = useState(() => Date.now() - 30 * 24 * 3600 * 1000);

  const stats = useMemo(() => {
    const last30 = reportRuns.filter((r) => {
      const t = Date.parse(r.run_timestamp);
      return !Number.isNaN(t) && t >= windowStart;
    });
    let s = 0;
    let e = 0;
    let f = 0;
    let totalRows = 0;
    let runs = 0;
    for (const r of last30) {
      const ds = deriveStatus(r);
      if (ds === 'SUCCESS') s++;
      else if (ds === 'EMPTY') e++;
      else f++;
      if (r.row_count != null) {
        totalRows += r.row_count;
        runs++;
      }
    }
    return {
      success: s,
      empty: e,
      failed: f,
      total: last30.length,
      avgRows: runs === 0 ? null : Math.round(totalRows / runs),
    };
  }, [reportRuns, windowStart]);

  if (configs.isLoading) return <p className="text-muted-foreground">Loading…</p>;
  if (!config) {
    return (
      <div className="space-y-4">
        <Link to="/reporting" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" /> Reports
        </Link>
        <p className="text-muted-foreground">No report found for <code>{reportId}</code>.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/reporting"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Reports
        </Link>
        <div className="flex items-end justify-between gap-4 flex-wrap mt-2">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              {config.report_name ?? config.report_id}
            </h1>
            <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
              <code className="font-mono text-xs">{config.report_id}</code>
              {config.client && (
                <>
                  <span>·</span>
                  <span>{config.client}</span>
                </>
              )}
              <span>·</span>
              {config.enabled === true ? (
                <Badge variant="success">Enabled</Badge>
              ) : (
                <Badge variant="secondary">Disabled</Badge>
              )}
            </div>
          </div>
          <Button onClick={() => setPreviewOpen(true)}>
            <Play className="h-4 w-4" />
            Preview
          </Button>
        </div>
      </div>

      {previewOpen && (
        <PreviewModal
          reportId={config.report_id}
          reportName={config.report_name ?? config.report_id}
          onClose={() => setPreviewOpen(false)}
        />
      )}

      <div className="grid gap-4 md:grid-cols-4">
        <StatTile
          icon={<CheckCircle2 className="h-4 w-4 text-success" />}
          label="Success (30d)"
          value={stats.success}
        />
        <StatTile
          icon={<AlertTriangle className="h-4 w-4 text-warning" />}
          label="Empty (30d)"
          value={stats.empty}
          emphasize={stats.empty > 0}
          emphasisClass="text-warning"
        />
        <StatTile
          icon={<XCircle className="h-4 w-4 text-destructive" />}
          label="Failed (30d)"
          value={stats.failed}
          emphasize={stats.failed > 0}
          emphasisClass="text-destructive"
        />
        <StatTile
          icon={<FileBarChart className="h-4 w-4 text-muted-foreground" />}
          label="Avg rows / run"
          value={stats.avgRows ?? '—'}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              Schedule
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-foreground">{humanizeSchedule(config.schedule)}</p>
            <p className="text-xs text-muted-foreground">
              Runs at 5:00 AM Pacific when the schedule matches. Raw value:{' '}
              <code className="font-mono">{config.schedule ?? '* * *'}</code>
            </p>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Repeat className="h-3 w-3" />
              Date range: last {config.date_range_days ?? 1}{' '}
              {(config.date_range_days ?? 1) === 1 ? 'day' : 'days'}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              Recipients ({config.recipients?.length ?? 0})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {config.recipients && config.recipients.length > 0 ? (
              <ul className="space-y-1">
                {config.recipients.map((r) => (
                  <li key={r} className="font-mono text-xs">
                    {r}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">No recipients configured.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Tag className="h-4 w-4 text-muted-foreground" />
            Configuration
          </CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <ConfigRow label="Report ID" value={<code className="font-mono">{config.report_id}</code>} />
            <ConfigRow label="Template" value={<code className="font-mono">{config.report_template ?? '—'}</code>} />
            <ConfigRow
              label="Client"
              value={
                config.client ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                    {config.client}
                  </span>
                ) : (
                  '—'
                )
              }
            />
            <ConfigRow label="Email subject" value={<code className="font-mono text-xs">{config.email_subject ?? '—'}</code>} />
          </dl>
        </CardContent>
      </Card>

      <Card className="overflow-hidden">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Run history</CardTitle>
        </CardHeader>
        {runs.error && (
          <div className="px-6 pb-3 text-sm text-destructive">{(runs.error as Error).message}</div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-y border-border">
              <tr>
                <SortableHeader
                  label="When"
                  sortKey="run_timestamp"
                  sort={sort}
                  onToggle={toggle}
                />
                <SortableHeader
                  label="Target date"
                  sortKey="report_date"
                  sort={sort}
                  onToggle={toggle}
                />
                <SortableHeader label="Status" sortKey="status" sort={sort} onToggle={toggle} />
                <SortableHeader label="Rows" sortKey="row_count" sort={sort} onToggle={toggle} />
                <SortableHeader
                  label="Duration"
                  sortKey="duration_ms"
                  sort={sort}
                  onToggle={toggle}
                />
                <SortableHeader
                  label="Trigger"
                  sortKey="triggered_by"
                  sort={sort}
                  onToggle={toggle}
                />
              </tr>
            </thead>
            <tbody>
              {runs.isLoading && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              )}
              {sortedItems.map((r) => {
                const ds = deriveStatus(r);
                const empty = ds === 'EMPTY';
                return (
                  <tr
                    key={r.run_timestamp}
                    onClick={() => setSelected(r)}
                    className="border-b border-border last:border-0 hover:bg-muted/50 transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-3">
                      <div className="text-foreground">{formatRelative(r.run_timestamp)}</div>
                      <div className="text-xs text-muted-foreground font-mono">
                        {formatDateTime(r.run_timestamp)}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                      {r.report_date ?? '—'}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={DISPLAY_VARIANT[ds]}>{DISPLAY_LABEL[ds]}</Badge>
                    </td>
                    <td
                      className={cn(
                        'px-4 py-3 tabular-nums inline-flex items-center gap-1',
                        empty && 'text-warning font-medium',
                      )}
                    >
                      {empty && <AlertTriangle className="h-3.5 w-3.5" />}
                      {formatNumber(r.row_count)}
                    </td>
                    <td className="px-4 py-3 tabular-nums text-muted-foreground">
                      {r.duration_ms == null ? '—' : `${(r.duration_ms / 1000).toFixed(1)}s`}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground capitalize">
                      {r.triggered_by ?? '—'}
                    </td>
                  </tr>
                );
              })}
              {!runs.isLoading && sortedItems.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">
                    No runs recorded for this report yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {selected && <RunDrawer run={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function StatTile({
  icon,
  label,
  value,
  emphasize,
  emphasisClass,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  emphasize?: boolean;
  emphasisClass?: string;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-xs font-medium">{label}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        <div className={cn('text-2xl font-bold tabular-nums', emphasize && emphasisClass)}>
          {value}
        </div>
      </CardContent>
    </Card>
  );
}

function ConfigRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="mt-1">{value}</dd>
    </div>
  );
}

function RunDrawer({ run, onClose }: { run: ReportRun; onClose: () => void }) {
  const ds = deriveStatus(run);
  const { can } = useAuth();
  const canPreview = can('reporting.preview');
  const canRun = can('reporting.run');

  const [previewOpen, setPreviewOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-foreground/40 backdrop-blur-sm" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 w-full max-w-2xl bg-card border-l border-border shadow-xl overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-lg font-semibold">{run.report_id}</h2>
            <p className="text-xs text-muted-foreground">{formatDateTime(run.run_timestamp)}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
            <X className="h-5 w-5" />
          </Button>
        </div>
        <div className="p-6 space-y-5">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant={DISPLAY_VARIANT[ds]}>{DISPLAY_LABEL[ds]}</Badge>
            {run.triggered_by && (
              <Badge variant="outline" className="capitalize">
                {run.triggered_by}
              </Badge>
            )}
            {run.report_date && (
              <span className="text-sm text-muted-foreground">target {run.report_date}</span>
            )}
          </div>

          <div className="flex gap-2 flex-wrap">
            {canPreview && run.report_date && (
              <Button variant="outline" onClick={() => setPreviewOpen(true)}>
                <Play className="h-4 w-4" />
                Preview data
              </Button>
            )}
            {canRun && run.report_date && (
              <Button variant="outline" onClick={() => setSendOpen(true)}>
                <Send className="h-4 w-4" />
                Re-run + send
              </Button>
            )}
          </div>

          {ds === 'EMPTY' && (
            <div className="rounded-md border border-warning/40 bg-warning/10 text-warning px-4 py-3 text-sm flex gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">Query returned 0 rows.</p>
                <p className="text-xs mt-1 opacity-90">
                  The Athena query ran successfully but produced no results. If data has
                  backfilled since, click <span className="font-medium">Preview data</span> to
                  check, then <span className="font-medium">Re-run + send</span> to resend the
                  email.
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <Mini
              label="Rows"
              value={formatNumber(run.row_count)}
              icon={<FileBarChart className="h-4 w-4 text-muted-foreground" />}
            />
            <Mini
              label="Duration"
              value={run.duration_ms == null ? '—' : `${(run.duration_ms / 1000).toFixed(1)}s`}
              icon={<Clock className="h-4 w-4 text-muted-foreground" />}
            />
            <Mini
              label="Recipients"
              value={formatNumber(run.recipients?.length)}
              icon={<Mail className="h-4 w-4 text-muted-foreground" />}
            />
          </div>

          <section>
            <h3 className="text-sm font-medium mb-2 flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              Recipients ({run.recipients?.length ?? 0})
            </h3>
            <Card>
              <CardContent className="pt-6">
                {run.recipients && run.recipients.length > 0 ? (
                  <ul className="space-y-1 text-sm">
                    {run.recipients.map((r) => (
                      <li key={r} className="font-mono text-xs">
                        {r}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    No recipients recorded for this run.
                  </p>
                )}
              </CardContent>
            </Card>
          </section>

          {run.error && (
            <section>
              <h3 className="text-sm font-medium mb-2 flex items-center gap-2">
                <XCircle className="h-4 w-4 text-destructive" />
                Error
              </h3>
              <Card className="border-destructive/40">
                <CardContent className="pt-6">
                  <pre className="text-xs text-destructive whitespace-pre-wrap font-mono">
                    {run.error}
                  </pre>
                </CardContent>
              </Card>
            </section>
          )}

          {ds === 'SUCCESS' && !run.error && (
            <div className="flex items-center gap-2 text-sm text-success">
              <CheckCircle2 className="h-4 w-4" />
              Email delivered successfully.
            </div>
          )}
        </div>
      </div>

      {previewOpen && run.report_date && (
        <PreviewModal
          reportId={run.report_id}
          reportName={run.report_id}
          initialDate={run.report_date}
          autoRun
          onClose={() => setPreviewOpen(false)}
        />
      )}
      {sendOpen && run.report_date && (
        <SendReportModal
          reportId={run.report_id}
          reportName={run.report_id}
          date={run.report_date}
          onClose={() => setSendOpen(false)}
        />
      )}
    </div>
  );
}

function Mini({ label, value, icon }: { label: string; value: React.ReactNode; icon: React.ReactNode }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-xs font-medium">{label}</CardTitle>
        {icon}
      </CardHeader>
      <CardContent>
        <div className="text-lg font-bold tabular-nums">{value}</div>
      </CardContent>
    </Card>
  );
}
